import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  useToast,
  IconButton,
  Tooltip,
  Divider,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Stack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  Flex,
  Spacer,
  Link,
  Alert,
  AlertIcon,
  Spinner,
} from '@chakra-ui/react';
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  DownloadIcon,
  EmailIcon,
  ViewIcon,
  SearchIcon,
  ExternalLinkIcon,
} from '@chakra-ui/icons';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { api } from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';

interface Invoice {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  customerName: string;
  customerICO: string;
  customerAddress?: string;
  customerZIP?: string;
  customerCity?: string;
  supplierName?: string;
  supplierICO?: string;
  supplierAddress?: string;
  supplierZIP?: string;
  supplierCity?: string;
  totalAmount: number;
  totalAmountVAT: number;
  totalAmountWithoutVAT?: number;
  totalVAT?: number;
  currency: string;
  note?: string;
  paymentMethod?: string;
  createdAt: string;
  updatedAt: string;
}

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  totalPrice: number;
  vatAmount: number;
  totalWithVat: number;
}

interface Customer {
  id: string;
  name: string;
  ico: string;
  dic: string;
  address: string;
  city: string;
  zip: string;
  country: string;
  email: string;
  phone: string;
  bankAccount: string;
  iban: string;
  swift: string;
}

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [formData, setFormData] = useState({
    customerId: '',
    issueDate: format(new Date(), 'yyyy-MM-dd'),
    dueDate: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    currency: 'CZK',
    note: '',
    paymentMethod: 'bank_transfer',
  });

  const [customerFormData, setCustomerFormData] = useState({
    name: '',
    ico: '',
    dic: '',
    address: '',
    city: '',
    zip: '',
    country: 'Česká republika',
    email: '',
    phone: '',
    bankAccount: '',
    iban: '',
    swift: '',
  });

  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [isSearchingICO, setIsSearchingICO] = useState(false);

  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const toast = useToast();

  // Load invoices and customers
  useEffect(() => {
    loadInvoices();
    loadCustomers();
  }, []);

  const loadInvoices = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/invoices');
      setInvoices(response.data.invoices || []);
    } catch (error) {
      toast({
        title: 'Chyba při načítání faktur',
        description: 'Nepodařilo se načíst seznam faktur',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/admin/invoices/customers');
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const searchCompanyByICO = async (ico: string) => {
    if (!ico || ico.length !== 8) {
      return;
    }

    try {
      setIsSearchingICO(true);
      
      // Use the Czech company registry API (ARES)
      const response = await fetch(`https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/${ico}`);
      
      if (!response.ok) {
        throw new Error('Company not found');
      }
      
      const data = await response.json();
      
      if (data) {
        // Update form with company data
        setCustomerFormData(prev => ({
          ...prev,
          name: data.obchodniJmeno || prev.name,
          dic: data.dic || prev.dic,
          address: data.sidlo && data.sidlo.textovaAdresa ? data.sidlo.textovaAdresa : prev.address,
          city: data.sidlo && data.sidlo.nazevObce ? data.sidlo.nazevObce : prev.city,
          zip: data.sidlo && data.sidlo.psc ? data.sidlo.psc : prev.zip,
          country: data.sidlo && data.sidlo.nazevStatu ? data.sidlo.nazevStatu : prev.country,
        }));
        
        toast({
          title: 'Údaje nalezeny',
          description: `Společnost ${data.obchodniJmeno} byla automaticky vyplněna`,
          status: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error searching company:', error);
      toast({
        title: 'Společnost nenalezena',
        description: 'Pro zadané IČO se nepodařilo najít údaje. Zkontrolujte IČO nebo vyplňte údaje ručně.',
        status: 'warning',
        duration: 3000,
      });
    } finally {
      setIsSearchingICO(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!customerFormData.name || !customerFormData.ico) {
      toast({
        title: 'Chyba',
        description: 'Vyplňte název a IČO odběratele',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setIsCreatingCustomer(true);
      const response = await api.post('/admin/invoices/customers', customerFormData);
      
      toast({
        title: 'Odběratel vytvořen',
        description: `Odběratel ${customerFormData.name} byl úspěšně vytvořen`,
        status: 'success',
        duration: 3000,
      });
      
      // Reset form and close modal
      setCustomerFormData({
        name: '',
        ico: '',
        dic: '',
        address: '',
        city: '',
        zip: '',
        country: 'Česká republika',
        email: '',
        phone: '',
        bankAccount: '',
        iban: '',
        swift: '',
      });
      setIsCustomerModalOpen(false);
      
      // Reload customers and select the new one
      await loadCustomers();
      if (response.data.customer?.id) {
        setFormData({ ...formData, customerId: response.data.customer.id });
      }
    } catch (error: any) {
      toast({
        title: 'Chyba při vytváření odběratele',
        description: error?.response?.data?.message || 'Nepodařilo se vytvořit odběratele',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  const updateCustomerFormData = (field: keyof typeof customerFormData, value: any) => {
    setCustomerFormData({ ...customerFormData, [field]: value });
  };

  const handleCreateInvoice = async () => {
    if (!formData.customerId) {
      toast({
        title: 'Chyba',
        description: 'Vyberte odběratele',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (invoiceItems.length === 0) {
      toast({
        title: 'Chyba',
        description: 'Přidejte alespoň jednu položku',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      await api.post('/admin/invoices', {
        ...formData,
        items: invoiceItems,
      });

      toast({
        title: 'Faktura vytvořena',
        description: 'Faktura byla úspěšně vytvořena',
        status: 'success',
        duration: 3000,
      });
      setIsCreateModalOpen(false);
      resetForm();
      loadInvoices();
    } catch (error: any) {
      toast({
        title: 'Chyba při vytváření faktury',
        description: error?.response?.data?.message || 'Nepodařilo se vytvořit fakturu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleUpdateInvoice = async () => {
    if (!selectedInvoice) return;

    try {
      await api.put(`/admin/invoices/${selectedInvoice.id}`, {
        ...formData,
        items: invoiceItems,
      });

      toast({
        title: 'Faktura aktualizována',
        description: 'Faktura byla úspěšně aktualizována',
        status: 'success',
        duration: 3000,
      });
      setIsEditModalOpen(false);
      resetForm();
      loadInvoices();
    } catch (error: any) {
      toast({
        title: 'Chyba při aktualizaci faktury',
        description: error?.response?.data?.message || 'Nepodařilo se aktualizovat fakturu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeleteInvoice = async (id: string) => {
    try {
      await api.delete(`/admin/invoices/${id}`);

      toast({
        title: 'Faktura smazána',
        description: 'Faktura byla úspěšně smazána',
        status: 'success',
        duration: 3000,
      });
      onDeleteClose();
      loadInvoices();
    } catch (error: any) {
      toast({
        title: 'Chyba při mazání faktury',
        description: error?.response?.data?.message || 'Nepodařilo se smazat fakturu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleGeneratePDF = async (id: string) => {
    try {
      const response = await api.post(`/admin/invoices/${id}/generate-pdf`);
      
      toast({
        title: 'PDF vygenerováno',
        description: 'PDF faktura byla úspěšně vygenerována',
        status: 'success',
        duration: 3000,
      });
      // Open PDF in new tab
      window.open(response.data.filePath, '_blank');
    } catch (error: any) {
      toast({
        title: 'Chyba při generování PDF',
        description: error?.response?.data?.message || 'Nepodařilo se vygenerovat PDF',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleSendInvoice = async (id: string) => {
    try {
      await api.post(`/admin/invoices/${id}/send`);

      toast({
        title: 'Faktura odeslána',
        description: 'Faktura byla úspěšně odeslána emailem',
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Chyba při odesílání faktury',
        description: error?.response?.data?.message || 'Nepodařilo se odeslat fakturu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const addInvoiceItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      vatRate: 21,
      totalPrice: 0,
      vatAmount: 0,
      totalWithVat: 0,
    };
    setInvoiceItems([...invoiceItems, newItem]);
  };

  const updateInvoiceItem = (id: string, field: keyof InvoiceItem, value: any) => {
    setInvoiceItems(invoiceItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        // Recalculate totals
        if (field === 'quantity' || field === 'unitPrice' || field === 'vatRate') {
          updatedItem.totalPrice = updatedItem.quantity * updatedItem.unitPrice;
          updatedItem.vatAmount = updatedItem.totalPrice * (updatedItem.vatRate / 100);
          updatedItem.totalWithVat = updatedItem.totalPrice + updatedItem.vatAmount;
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  const removeInvoiceItem = (id: string) => {
    setInvoiceItems(items => items.filter(item => item.id !== id));
  };

  const resetForm = () => {
    setFormData({
      customerId: '',
      issueDate: format(new Date(), 'yyyy-MM-dd'),
      dueDate: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      currency: 'CZK',
      note: '',
      paymentMethod: 'bank_transfer',
    });
    setInvoiceItems([]);
    setSelectedInvoice(null);
  };

  const openEditModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setFormData({
      customerId: invoice.customerICO || '',
      issueDate: invoice.issueDate,
      dueDate: invoice.dueDate,
      currency: invoice.currency,
      note: invoice.note || '',
      paymentMethod: invoice.paymentMethod || 'bank_transfer',
    });
    // Load invoice items
    loadInvoiceItems(invoice.id);
    setIsEditModalOpen(true);
  };

  const loadInvoiceItems = async (invoiceId: string) => {
    try {
      const response = await api.get(`/admin/invoices/${invoiceId}`);
      setInvoiceItems(response.data.invoice.items || []);
    } catch (error) {
      console.error('Error loading invoice items:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'gray';
      case 'sent': return 'blue';
      case 'paid': return 'green';
      case 'overdue': return 'red';
      case 'cancelled': return 'orange';
      default: return 'gray';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return 'Návrh';
      case 'sent': return 'Odeslána';
      case 'paid': return 'Zaplacena';
      case 'overdue': return 'Po splatnosti';
      case 'cancelled': return 'Zrušena';
      default: return status;
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const calculateTotal = () => {
    return invoiceItems.reduce((sum, item) => sum + item.totalWithVat, 0);
  };

  return (
    <AdminLayout>
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Heading size="lg">Fakturace</Heading>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => {
              resetForm();
              setIsCreateModalOpen(true);
            }}
          >
            Nová faktura
          </Button>
        </Flex>

        {/* Filters */}
        <Card>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl>
                <FormLabel>Vyhledávání</FormLabel>
                <InputGroup>
                  <InputLeftElement>
                    <SearchIcon />
                  </InputLeftElement>
                  <Input
                    placeholder="Hledat podle čísla faktury nebo odběratele..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel>Stav</FormLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Všechny stavy</option>
                  <option value="draft">Návrh</option>
                  <option value="sent">Odeslána</option>
                  <option value="paid">Zaplacena</option>
                  <option value="overdue">Po splatnosti</option>
                  <option value="cancelled">Zrušena</option>
                </Select>
              </FormControl>
              <Box />
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Invoices Table */}
        <Card overflow="hidden">
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Číslo faktury</Th>
                  <Th>Datum vystavení</Th>
                  <Th>Datum splatnosti</Th>
                  <Th>Odběratel</Th>
                  <Th>Částka</Th>
                  <Th>Stav</Th>
                  <Th>Akce</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredInvoices.map((invoice) => (
                  <Tr key={invoice.id}>
                    <Td fontWeight="medium">{invoice.invoiceNumber}</Td>
                    <Td>{format(new Date(invoice.issueDate), 'dd.MM.yyyy', { locale: cs })}</Td>
                    <Td>{format(new Date(invoice.dueDate), 'dd.MM.yyyy', { locale: cs })}</Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{invoice.customerName}</Text>
                        <Text fontSize="sm" color="gray.600">{invoice.customerICO}</Text>
                      </VStack>
                    </Td>
                    <Td fontWeight="medium">
                      {invoice.totalAmountVAT.toFixed(2)} {invoice.currency}
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(invoice.status)}>
                        {getStatusText(invoice.status)}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label="Zobrazit">
                          <IconButton
                            icon={<ViewIcon />}
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              loadInvoiceItems(invoice.id);
                              setIsViewModalOpen(true);
                            }}
                            aria-label="Zobrazit fakturu"
                          />
                        </Tooltip>
                        <Tooltip label="Upravit">
                          <IconButton
                            icon={<EditIcon />}
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(invoice)}
                            aria-label="Upravit fakturu"
                          />
                        </Tooltip>
                        <Tooltip label="Generovat PDF">
                          <IconButton
                            icon={<DownloadIcon />}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleGeneratePDF(invoice.id)}
                            aria-label="Generovat PDF faktury"
                          />
                        </Tooltip>
                        <Tooltip label="Odeslat emailem">
                          <IconButton
                            icon={<EmailIcon />}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendInvoice(invoice.id)}
                            aria-label="Odeslat fakturu emailem"
                          />
                        </Tooltip>
                        <Tooltip label="Smazat">
                          <IconButton
                            icon={<DeleteIcon />}
                            variant="ghost"
                            size="sm"
                            colorScheme="red"
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setIsDeleteModalOpen(true);
                            }}
                            aria-label="Smazat fakturu"
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={isCreateModalOpen || isEditModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            resetForm();
          }}
          size="4xl"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {isCreateModalOpen ? 'Vytvořit fakturu' : 'Upravit fakturu'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Odběratel</FormLabel>
                    <Select
                      value={formData.customerId}
                      onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                      placeholder="Vyberte odběratele"
                    >
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name} ({customer.ico})
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Měna</FormLabel>
                    <Select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    >
                      <option value="CZK">CZK</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </Select>
                  </FormControl>
                  <FormControl display="flex" alignItems="flex-end">
                    <Button
                      leftIcon={<AddIcon />}
                      colorScheme="green"
                      onClick={() => setIsCustomerModalOpen(true)}
                      width="full"
                    >
                      Přidat odběratele
                    </Button>
                  </FormControl>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Datum vystavení</FormLabel>
                    <Input
                      type="date"
                      value={formData.issueDate}
                      onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Datum splatnosti</FormLabel>
                    <Input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel>Způsob platby</FormLabel>
                  <Select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  >
                    <option value="bank_transfer">Bankovní převod</option>
                    <option value="cash">Hotově</option>
                    <option value="card">Kartou</option>
                    <option value="other">Jiný</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Poznámka</FormLabel>
                  <Textarea
                    value={formData.note}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    placeholder="Poznámka k faktuře..."
                    rows={3}
                  />
                </FormControl>

                <Divider />

                <Flex justify="space-between" align="center">
                  <Heading size="md">Položky faktury</Heading>
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="blue"
                    size="sm"
                    onClick={addInvoiceItem}
                  >
                    Přidat položku
                  </Button>
                </Flex>

                <VStack spacing={3} align="stretch">
                  {invoiceItems.map((item, index) => (
                    <Card key={item.id} size="sm">
                      <CardBody>
                        <SimpleGrid columns={{ base: 1, md: 6 }} spacing={3} alignItems="end">
                          <FormControl>
                            <FormLabel fontSize="sm">Popis</FormLabel>
                            <Input
                              value={item.description}
                              onChange={(e) => updateInvoiceItem(item.id, 'description', e.target.value)}
                              placeholder="Popis položky..."
                              size="sm"
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Množství</FormLabel>
                            <NumberInput
                              value={item.quantity}
                              onChange={(value) => updateInvoiceItem(item.id, 'quantity', parseFloat(value) || 0)}
                              min={0}
                              step={1}
                              size="sm"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Cena/jedn.</FormLabel>
                            <NumberInput
                              value={item.unitPrice}
                              onChange={(value) => updateInvoiceItem(item.id, 'unitPrice', parseFloat(value) || 0)}
                              min={0}
                              step={0.01}
                              size="sm"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">DPH %</FormLabel>
                            <Select
                              value={item.vatRate}
                              onChange={(e) => updateInvoiceItem(item.id, 'vatRate', parseFloat(e.target.value))}
                              size="sm"
                            >
                              <option value="0">0%</option>
                              <option value="21">21%</option>
                              <option value="15">15%</option>
                              <option value="10">10%</option>
                            </Select>
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Celkem s DPH</FormLabel>
                            <Input
                              value={item.totalWithVat.toFixed(2)}
                              isReadOnly
                              size="sm"
                              textAlign="right"
                            />
                          </FormControl>
                          <IconButton
                            icon={<DeleteIcon />}
                            variant="ghost"
                            size="sm"
                            colorScheme="red"
                            onClick={() => removeInvoiceItem(item.id)}
                            aria-label="Smazat položku faktury"
                          />
                        </SimpleGrid>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>

                {invoiceItems.length > 0 && (
                  <Card>
                    <CardBody>
                      <Flex justify="space-between" align="center">
                        <Text fontWeight="bold">Celková částka:</Text>
                        <Text fontSize="xl" fontWeight="bold">
                          {calculateTotal().toFixed(2)} {formData.currency}
                        </Text>
                      </Flex>
                    </CardBody>
                  </Card>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="outline"
                mr={3}
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setIsEditModalOpen(false);
                  resetForm();
                }}
              >
                Zrušit
              </Button>
              <Button
                colorScheme="blue"
                onClick={isCreateModalOpen ? handleCreateInvoice : handleUpdateInvoice}
                isLoading={isLoading}
              >
                {isCreateModalOpen ? 'Vytvořit' : 'Uložit'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* View Modal */}
        <Modal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          size="4xl"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              Faktura č. {selectedInvoice?.invoiceNumber}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedInvoice && (
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Card>
                      <CardHeader>
                        <Heading size="sm">Dodavatel</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold">{selectedInvoice.supplierName}</Text>
                          <Text>IČO: {selectedInvoice.supplierICO}</Text>
                          <Text>{selectedInvoice.supplierAddress}</Text>
                          <Text>{selectedInvoice.supplierZIP} {selectedInvoice.supplierCity}</Text>
                        </VStack>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardHeader>
                        <Heading size="sm">Odběratel</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold">{selectedInvoice.customerName}</Text>
                          <Text>IČO: {selectedInvoice.customerICO}</Text>
                          <Text>{selectedInvoice.customerAddress}</Text>
                          <Text>{selectedInvoice.customerZIP} {selectedInvoice.customerCity}</Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Card>
                      <CardBody>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" color="gray.600">Číslo faktury</Text>
                          <Text fontWeight="bold">{selectedInvoice.invoiceNumber}</Text>
                        </VStack>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" color="gray.600">Datum vystavení</Text>
                          <Text fontWeight="bold">
                            {format(new Date(selectedInvoice.issueDate), 'dd.MM.yyyy', { locale: cs })}
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" color="gray.600">Datum splatnosti</Text>
                          <Text fontWeight="bold">
                            {format(new Date(selectedInvoice.dueDate), 'dd.MM.yyyy', { locale: cs })}
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <Card>
                    <CardHeader>
                      <Heading size="sm">Položky faktury</Heading>
                    </CardHeader>
                    <CardBody>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Popis</Th>
                            <Th textAlign="right">Množství</Th>
                            <Th textAlign="right">Cena/jedn.</Th>
                            <Th textAlign="right">DPH %</Th>
                            <Th textAlign="right">Celkem s DPH</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {invoiceItems.map((item) => (
                            <Tr key={item.id}>
                              <Td>{item.description}</Td>
                              <Td textAlign="right">{item.quantity}</Td>
                              <Td textAlign="right">{item.unitPrice.toFixed(2)} {selectedInvoice.currency}</Td>
                              <Td textAlign="right">{item.vatRate}%</Td>
                              <Td textAlign="right">{item.totalWithVat.toFixed(2)} {selectedInvoice.currency}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <Flex justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold">Celkem bez DPH:</Text>
                          <Text fontWeight="bold">DPH:</Text>
                          <Text fontSize="lg" fontWeight="bold">Celkem k úhradě:</Text>
                        </VStack>
                        <VStack align="end" spacing={1}>
                          <Text>{(selectedInvoice.totalAmountWithoutVAT || 0).toFixed(2)} {selectedInvoice.currency}</Text>
                          <Text>{(selectedInvoice.totalVAT || 0).toFixed(2)} {selectedInvoice.currency}</Text>
                          <Text fontSize="lg" fontWeight="bold">
                            {selectedInvoice.totalAmountVAT.toFixed(2)} {selectedInvoice.currency}
                          </Text>
                        </VStack>
                      </Flex>
                    </CardBody>
                  </Card>

                  {selectedInvoice.note && (
                    <Card>
                      <CardHeader>
                        <Heading size="sm">Poznámka</Heading>
                      </CardHeader>
                      <CardBody>
                        <Text>{selectedInvoice.note}</Text>
                      </CardBody>
                    </Card>
                  )}
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                variant="outline"
                mr={3}
                onClick={() => setIsViewModalOpen(false)}
              >
                Zavřít
              </Button>
              {selectedInvoice && (
                <>
                  <Button
                    leftIcon={<DownloadIcon />}
                    colorScheme="blue"
                    mr={3}
                    onClick={() => handleGeneratePDF(selectedInvoice.id)}
                  >
                    Generovat PDF
                  </Button>
                  <Button
                    leftIcon={<EmailIcon />}
                    colorScheme="green"
                    onClick={() => handleSendInvoice(selectedInvoice.id)}
                  >
                    Odeslat emailem
                  </Button>
                </>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Customer Creation Modal */}
        <Modal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          size="2xl"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Přidat nového odběratele</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Název firmy</FormLabel>
                    <Input
                      value={customerFormData.name}
                      onChange={(e) => updateCustomerFormData('name', e.target.value)}
                      placeholder="Název firmy"
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>IČO</FormLabel>
                    <InputGroup>
                      <Input
                        value={customerFormData.ico}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                          updateCustomerFormData('ico', value);
                          if (value.length === 8) {
                            searchCompanyByICO(value);
                          }
                        }}
                        placeholder="12345678"
                        maxLength={8}
                      />
                      {isSearchingICO && (
                        <InputRightElement>
                          <Spinner size="sm" />
                        </InputRightElement>
                      )}
                    </InputGroup>
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>DIČ</FormLabel>
                    <Input
                      value={customerFormData.dic}
                      onChange={(e) => updateCustomerFormData('dic', e.target.value)}
                      placeholder="CZ1234567890"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Stát</FormLabel>
                    <Input
                      value={customerFormData.country}
                      onChange={(e) => updateCustomerFormData('country', e.target.value)}
                      placeholder="Česká republika"
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel>Adresa</FormLabel>
                  <Input
                    value={customerFormData.address}
                    onChange={(e) => updateCustomerFormData('address', e.target.value)}
                    placeholder="Adresa"
                  />
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Město</FormLabel>
                    <Input
                      value={customerFormData.city}
                      onChange={(e) => updateCustomerFormData('city', e.target.value)}
                      placeholder="Město"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>PSČ</FormLabel>
                    <Input
                      value={customerFormData.zip}
                      onChange={(e) => updateCustomerFormData('zip', e.target.value)}
                      placeholder="123 45"
                    />
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={customerFormData.email}
                      onChange={(e) => updateCustomerFormData('email', e.target.value)}
                      placeholder="email@firma.cz"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Telefon</FormLabel>
                    <Input
                      value={customerFormData.phone}
                      onChange={(e) => updateCustomerFormData('phone', e.target.value)}
                      placeholder="+420 123 456 789"
                    />
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Číslo účtu</FormLabel>
                    <Input
                      value={customerFormData.bankAccount}
                      onChange={(e) => updateCustomerFormData('bankAccount', e.target.value)}
                      placeholder="123456789/0300"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>IBAN</FormLabel>
                    <Input
                      value={customerFormData.iban}
                      onChange={(e) => updateCustomerFormData('iban', e.target.value)}
                      placeholder="CZ12345678901234567890123456"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>SWIFT/BIC</FormLabel>
                    <Input
                      value={customerFormData.swift}
                      onChange={(e) => updateCustomerFormData('swift', e.target.value)}
                      placeholder="GIBACZPX"
                    />
                  </FormControl>
                </SimpleGrid>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="outline"
                mr={3}
                onClick={() => setIsCustomerModalOpen(false)}
              >
                Zrušit
              </Button>
              <Button
                colorScheme="green"
                onClick={handleCreateCustomer}
                isLoading={isCreatingCustomer}
              >
                Vytvořit odběratele
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Smazat fakturu</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Alert status="warning">
                <AlertIcon />
                Opravdu chcete smazat fakturu č. {selectedInvoice?.invoiceNumber}?
                Tuto akci nelze vrátit zpět.
              </Alert>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onDeleteClose}>
                Zrušit
              </Button>
              <Button
                colorScheme="red"
                onClick={() => selectedInvoice && handleDeleteInvoice(selectedInvoice.id)}
              >
                Smazat
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        </VStack>
      </Container>
    </AdminLayout>
  );
};

export default InvoicesPage;
